import type {
  U3202ActivatedModelRow,
  U3202OrgProviderConfigFormData,
  U3202ProviderCreate,
  U3202ProviderUpdate,
  U3202ParameterSchemaDefinition,
} from '../U3202Service';
import { u3202RowToModelInput } from '../U3202Service';

export function u3202MergeParameterValues(
  defaultParameters: Record<string, unknown>,
  parameterOverrides: Record<string, unknown>,
): Record<string, unknown> {
  return { ...defaultParameters, ...parameterOverrides };
}

export function u3202ExtractParameterDefaults(
  parameterSchema: U3202ParameterSchemaDefinition | null,
  mergedValues: Record<string, unknown>,
): Record<string, unknown> {
  if (!parameterSchema) return {};
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(parameterSchema)) {
    if (Object.prototype.hasOwnProperty.call(mergedValues, key)) {
      result[key] = mergedValues[key];
    }
  }
  return result;
}

export function u3202BuildProviderCreateBody(data: U3202OrgProviderConfigFormData): U3202ProviderCreate {
  return {
    code: (data.newProviderCode ?? '').trim().toLowerCase(),
    name: (data.newProviderName ?? '').trim(),
    apiKey: data.apiKey.trim(),
    apiUrl: data.apiUrl.trim() !== '' ? data.apiUrl.trim() : null,
    showThinking: data.showThinking,
    active: true,
    models: data.models.map(u3202RowToModelInput),
  };
}

export function u3202BuildProviderUpdateBody(data: U3202OrgProviderConfigFormData): U3202ProviderUpdate {
  const body: U3202ProviderUpdate = {
    id: data.id,
    code: (data.providerCode ?? '').trim().toLowerCase(),
    name: (data.providerName ?? '').trim(),
    active: data.active,
    showThinking: data.showThinking,
    apiUrl: data.apiUrl.trim() !== '' ? data.apiUrl.trim() : null,
    version: data.version,
    models: data.models.map(u3202RowToModelInput),
  };
  if (data.apiKeyTouched && data.apiKey.trim() !== '') {
    body.apiKey = data.apiKey.trim();
  }
  return body;
}

/** @deprecated alias */
export const u3202BuildOrgProviderConfigUpdateBody = u3202BuildProviderUpdateBody;

export function u3202ValidateOrgProviderConfigForm(data: U3202OrgProviderConfigFormData, isCreate: boolean): Record<string, string> {
  const result: Record<string, string> = {};
  if (isCreate) {
    if (!data.newProviderCode?.trim()) result['newProviderCode'] = 'errProviderCodeRequired';
    if (!data.newProviderName?.trim()) result['newProviderName'] = 'errProviderNameRequired';
    if (!data.apiKey.trim()) result['apiKey'] = 'errApiKeyRequired';
  } else if (!data.hasExistingApiKey && !data.apiKey.trim()) {
    result['apiKey'] = 'errApiKeyRequired';
  }
  data.models.forEach((row, index) => {
    if (!row.active || !row.parameterSchema) return;
    for (const key of Object.keys(row.parameterSchema)) {
      const validation = u3202ValidateParameterValue(key, row.parameterValues[key], row.parameterSchema);
      if (validation === 'min' || validation === 'max') {
        result[`models.${index}.parameterValues.${key}`] = 'errParameterOutOfRange';
      } else if (validation) {
        result[`models.${index}.parameterValues.${key}`] = 'errInvalidParameter';
      }
    }
  });
  return result;
}

export function u3202ValidateParameterValue(
  fieldKey: string,
  value: unknown,
  schema: U3202ParameterSchemaDefinition,
): string | undefined {
  const field = schema[fieldKey];
  if (!field) return undefined;
  if (field.type === 'boolean') return typeof value === 'boolean' ? undefined : 'invalid';
  if (field.type === 'string') return typeof value === 'string' ? undefined : 'invalid';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return 'invalid';
  if (field.min !== undefined && num < field.min) return 'min';
  if (field.max !== undefined && num > field.max) return 'max';
  if (field.type === 'integer' && !Number.isInteger(num)) return 'integer';
  return undefined;
}

export function u3202ApplyModelDetailToRow(
  row: U3202ActivatedModelRow,
  modelDetail: {
    id: string;
    code: string;
    name: string;
    parameterSchema: U3202ParameterSchemaDefinition;
    defaultParameters: Record<string, unknown>;
  },
): U3202ActivatedModelRow {
  const defaultParameters = modelDetail.defaultParameters ?? {};
  return {
    ...row,
    modelCode: modelDetail.code,
    modelName: modelDetail.name,
    parameterSchema: modelDetail.parameterSchema,
    defaultParameters,
    parameterValues: u3202MergeParameterValues(defaultParameters, row.initialParameterDefaults),
  };
}
